import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-background-50 shadow-sm border-b border-background-200/70' : 'bg-background-50/95 border-b border-background-200/50'
    }`}>
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 tracking-tight">
              AllChinaBuy<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-xl border border-background-200/80 shadow-lg overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                  <div className="py-2 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-arrow-right-up-line text-foreground-400 text-xs"></i>
                        </div>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/article/allchinabuy-spreadsheet-complete-guide" className="text-sm font-semibold text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Shopping Guide
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold text-background-50 bg-foreground-900 hover:bg-foreground-800 rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              allchinabuy sheet
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`text-xl text-foreground-900 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/60 animate-[fadeIn_0.2s_ease-out]">
            <div className="pt-4 space-y-1">
              <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
                <i className="ri-home-4-line text-base text-foreground-500"></i>
                Home
              </Link>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-base text-foreground-500"></i>
                  Categories
                </span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="ml-3 pl-4 border-l-2 border-background-200/60 space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50/50 rounded-lg transition-colors cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-background-300 flex-shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <Link to="/article/allchinabuy-spreadsheet-complete-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-xl transition-colors cursor-pointer">
                <i className="ri-book-open-line text-base text-foreground-500"></i>
                Shopping Guide
              </Link>

              <div className="pt-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-background-50 bg-foreground-900 rounded-full hover:bg-foreground-800 transition-all cursor-pointer"
                >
                  allchinabuy sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}